import React from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaTruck, FaChartLine, FaGlobeAmericas } from 'react-icons/fa';
import CallToAction from '../components/shared/CallToAction';

const CapabilitiesPage = () => {
  const networkStats = [
    { number: "158", label: "Points de vente" },
    { number: "70", label: "Livraisons par semaine" },
    { number: "3", label: "Centrales d'achat partenaires" },
    { number: "2", label: "Jours de délai moyen de livraison" }
  ];

  const expansionTargets = [
    {
      name: "MAXI",
      logo: "/images/expansionTo/Maxi_logo.png",
      status: "En discussion"
    },
    {
      name: "IGA",
      logo: "/images/expansionTo/IGA_logo.png",
      status: "Phase pilote"
    },
    {
      name: "Loblaw",
      logo: "/images/expansionTo/Loblaws-Logo.png",
      status: "Planification"
    },
    {
      name: "Dollarama",
      logo: "/images/expansionTo/Dollarama_logo.png",
      status: "En négociation"
    }
  ];

  const newMarkets = [
    {
      name: "États-Unis",
      icon: <FaGlobeAmericas className="text-4xl text-primary mb-4" />,
      description: "Développement de partenariats avec des distributeurs américains pour faciliter l'entrée sur ce marché.",
      timeline: "Lancement prévu: 2026"
    },
    {
      name: "Nouveau-Brunswick",
      icon: <FaMapMarkerAlt className="text-4xl text-primary mb-4" />,
      description: "Extension de notre réseau de distribution aux provinces maritimes, en commençant par le Nouveau-Brunswick.",
      timeline: "Lancement prévu: 2026"
    },
    {
      name: "Nouvelle-Écosse",
      icon: <FaMapMarkerAlt className="text-4xl text-primary mb-4" />,
      description: "Poursuite de notre expansion dans les provinces maritimes avec la Nouvelle-Écosse.",
      timeline: "Lancement prévu: 2025-2026"
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <div className="bg-primary text-white py-16">
        <div className="container-custom">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Nos Capacités & Croissance</h1>
          <p className="text-xl max-w-2xl">
            Découvrez notre infrastructure logistique, notre réseau et nos objectifs de croissance pour les années à venir.
          </p>
        </div>
      </div>

      {/* Network & Logistics */}
      <section className="section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="section-title">Notre Réseau Logistique</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Un réseau de distribution efficace et en constante expansion pour servir le marché canadien.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {networkStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-lg shadow-md text-center"
              >
                <h3 className="text-4xl md:text-5xl font-bold text-primary mb-2">{stat.number}</h3>
                <p className="text-gray-600 text-lg">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold mb-4">Infrastructure Optimisée</h3>
              <p className="text-gray-600 mb-6">
                Notre réseau logistique est stratégiquement conçu pour assurer une distribution efficace et rapide dans tout l'est du Canada, avec des plans d'expansion continus.
              </p>
              
              <div className="space-y-4">
                <div className="bg-gray-100 p-6 rounded-lg">
                  <div className="flex items-start">
                    <div className="bg-primary/10 p-3 rounded-full mr-4">
                      <FaTruck className="text-primary text-xl" />
                    </div>
                    <div>
                      <h4 className="font-bold text-xl mb-2">Transport optimisé</h4>
                      <p className="text-gray-600">
                        Notre flotte de véhicules et nos partenaires de transport assurent des livraisons ponctuelles avec un suivi en temps réel.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-100 p-6 rounded-lg">
                  <div className="flex items-start">
                    <div className="bg-primary/10 p-3 rounded-full mr-4">
                      <FaMapMarkerAlt className="text-primary text-xl" />
                    </div>
                    <div>
                      <h4 className="font-bold text-xl mb-2">Couverture stratégique</h4>
                      <p className="text-gray-600">
                        Présence dans les principaux centres urbains avec une expansion progressive vers les marchés secondaires.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img 
                src="/images/logistics.png" 
                alt="DZF Distribution Logistics Network" 
                className="rounded-lg shadow-lg w-full h-auto" 
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Volume & Impact Metrics */}
      <section className="section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="section-title">Nos Performances en Chiffres</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Des indicateurs clés qui témoignent de notre croissance et de notre impact sur le marché.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <img 
                src="/images/DZFD_growth.png" 
                alt="DZF Distribution Growth Chart" 
                className="rounded-lg shadow-lg w-full h-auto" 
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <h3 className="text-2xl font-bold mb-4">Croissance Impressionnante</h3>
              <p className="text-gray-600 mb-6">
                Depuis notre création, nous avons connu une croissance exceptionnelle qui témoigne de la qualité de nos services et de la demande du marché.
              </p>
              
              <div className="space-y-4">
                <div className="bg-gray-100 p-6 rounded-lg">
                  <div className="flex items-center">
                    <div className="bg-primary/10 p-3 rounded-full mr-4">
                      <FaChartLine className="text-primary text-xl" />
                    </div>
                    <div>
                      <h4 className="font-bold text-xl">+250% de croissance des ventes</h4>
                      <p className="text-gray-600">Depuis 2023</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-100 p-6 rounded-lg">
                  <div className="flex items-center">
                    <div className="bg-primary/10 p-3 rounded-full mr-4">
                      <FaTruck className="text-primary text-xl" />
                    </div>
                    <div>
                      <h4 className="font-bold text-xl">+120% d'augmentation du volume</h4>
                      <p className="text-gray-600">En tonnage distribué depuis 2024</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-100 p-6 rounded-lg">
                  <div className="flex items-center">
                    <div className="bg-primary/10 p-3 rounded-full mr-4">
                      <FaMapMarkerAlt className="text-primary text-xl" />
                    </div>
                    <div>
                      <h4 className="font-bold text-xl">30% d'expansion géographique</h4>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="bg-primary/5 p-8 md:p-12 rounded-xl">
            <h3 className="text-2xl font-bold mb-6 text-center">Projections de croissance</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-lg shadow-md text-center"
              >
                <div className="text-3xl font-bold text-primary mb-2">+200</div>
                <p className="text-lg font-medium mb-3">Points de vente</p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-lg shadow-md text-center"
              >
                <div className="text-3xl font-bold text-primary mb-2">7</div>
                <p className="text-lg font-medium mb-3">Provinces</p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-lg shadow-md text-center"
              >
                <div className="text-3xl font-bold text-primary mb-2">+30%</div>
                <p className="text-lg font-medium mb-3">Revenu annuel</p>
                <p className="text-gray-600">Croissance prévue par an</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <CallToAction />
    </div>
  );
};

export default CapabilitiesPage; 